import React from 'react';
import { Card, CardContent } from './ui/card';
import {
  Database,
  Layers,
  FileSpreadsheet,
  Table2,
  GitMerge,
  Calendar,
  BarChart3,
  LineChart,
  LayoutDashboard,
  Ruler,
  Box,
  Bot,
} from 'lucide-react';

// Map node types to CSS variable names for consistent coloring
const NODE_TYPE_TO_CSS_VAR: Record<string, string> = {
  // Pipeline node types
  SOURCE: '--node-source',
  SOURCE_FILE: '--node-source',
  SEED_SOURCE: '--node-source',
  SEED: '--node-source',
  STAGING: '--node-staging',
  FACT: '--node-fact',
  DIMENSION: '--node-entity',      // Entity (PipelineNode with node_type=DIMENSION)
  DATE_DIMENSION: '--node-entity',
  CUSTOM: '--node-custom',
  MERGE: '--node-merge',
  REPORT: '--node-report',
  DESTINATION: '--node-destination',
  // Object types (for full tree display)
  MEASURE: '--node-metric',
  ANALYSIS: '--node-chart',
  DASHBOARD: '--node-dashboard',
  DIMENSION_ATTRIBUTE: '--node-dimension',  // Dimension (the model object)
  // Lowercase variants (used by AnalysisDependencyGraph)
  source: '--node-source',
  entity: '--node-entity',
  date_dimension: '--node-entity',
  metric: '--node-metric',
  dimension: '--node-dimension',
  report: '--node-report',
};

// Helper to get the computed CSS variable value
export function getNodeColor(nodeType: string): string {
  const cssVar = NODE_TYPE_TO_CSS_VAR[nodeType] || '--node-staging';
  if (typeof document !== 'undefined') {
    const value = getComputedStyle(document.documentElement).getPropertyValue(cssVar).trim();
    if (value) return value;
  }
  // Fallback colors if CSS variable not available
  const FALLBACK_COLORS: Record<string, string> = {
    '--node-source': '#6b7280',
    '--node-staging': '#6366f1',
    '--node-entity': '#f59e0b',
    '--node-fact': '#22c55e',
    '--node-report': '#ef4444',
    '--node-custom': '#8b5cf6',
    '--node-merge': '#ec4899',
    '--node-destination': '#06b6d4',
    '--node-metric': '#0891b2',
    '--node-dimension': '#a855f7',
    '--node-chart': '#10b981',
    '--node-dashboard': '#e11d48',
  };
  return FALLBACK_COLORS[cssVar] || '#6366f1';
}

// Helper to get the CSS variable name for a node type
export function getNodeCssVar(nodeType: string): string {
  return NODE_TYPE_TO_CSS_VAR[nodeType] || '--node-staging';
}

// Get icon component based on node type
export function getNodeIcon(nodeType: string, color: string, size: 'sm' | 'md' = 'sm') {
  const sizeClass = size === 'sm' ? 'h-4 w-4' : 'h-6 w-6';
  const iconClass = `${sizeClass} flex-shrink-0`;
  const style = { color };

  // Handle lowercase 'dimension' from AnalysisDependencyGraph specially
  // This refers to Dimension model objects, not PipelineNode entities with node_type=DIMENSION
  if (nodeType === 'dimension') {
    return <Ruler className={iconClass} style={style} />;
  }

  // Normalize to uppercase for comparison
  const type = nodeType.toUpperCase();

  switch (type) {
    case 'SOURCE':
    case 'SOURCE_FILE':
    case 'SEED_SOURCE':
      return <Database className={iconClass} style={style} />;
    case 'SEED':
      return <FileSpreadsheet className={iconClass} style={style} />;
    case 'STAGING':
      return <Layers className={iconClass} style={style} />;
    case 'FACT':
      return <Table2 className={iconClass} style={style} />;
    case 'DIMENSION':
    case 'ENTITY':
      return <Box className={iconClass} style={style} />;  // Entity (PipelineNode with node_type=DIMENSION)
    case 'DATE_DIMENSION':
      return <Calendar className={iconClass} style={style} />;
    case 'CUSTOM':
      return <GitMerge className={iconClass} style={style} />;
    case 'MERGE':
      return <GitMerge className={iconClass} style={style} />;
    case 'REPORT':
      return <Bot className={iconClass} style={style} />;
    case 'MEASURE':
    case 'METRIC':
      return <BarChart3 className={iconClass} style={style} />;
    case 'ANALYSIS':
    case 'CHART':
      return <LineChart className={iconClass} style={style} />;
    case 'DASHBOARD':
      return <LayoutDashboard className={iconClass} style={style} />;
    case 'DIMENSION_ATTRIBUTE':
      return <Ruler className={iconClass} style={style} />;
    default:
      return <Layers className={iconClass} style={style} />;
  }
}

export interface NodeCardProps {
  /** The title/label to display */
  title: string;
  /** The node type for color and icon selection */
  nodeType: string;
  /** Optional subtitle (e.g., type label) */
  subtitle?: string;
  /** Card width in pixels. If not set and fullWidth is false, defaults to 320 */
  width?: number;
  /** Card height in pixels */
  height?: number;
  /** If true, card fills its container width instead of using fixed width */
  fullWidth?: boolean;
  /** Icon size */
  iconSize?: 'sm' | 'md';
  /** Click handler */
  onClick?: () => void;
  /** Additional class names */
  className?: string;
  /** Custom content to render instead of icon (e.g., SourceIcon) */
  customIcon?: React.ReactNode;
  /** Children to render after the title (e.g., ReactFlow handles) */
  children?: React.ReactNode;
}

/**
 * Shared component for displaying nodes in tree diagrams and lists.
 * Uses consistent border-left styling and CSS variable colors.
 */
export const NodeCard: React.FC<NodeCardProps> = ({
  title,
  nodeType,
  subtitle,
  width,
  height = 48,
  fullWidth = false,
  iconSize = 'sm',
  onClick,
  className = '',
  customIcon,
  children,
}) => {
  const nodeColor = getNodeColor(nodeType);

  // Determine width: fullWidth uses 100%, explicit width uses that, otherwise default to 320
  const cardWidth = fullWidth ? '100%' : (width ?? 320);

  return (
    <Card
      className={`rounded-lg shadow-md relative flex items-center cursor-pointer hover:bg-accent transition-colors ${className}`}
      style={{
        width: cardWidth,
        height,
        borderLeft: `4px solid ${nodeColor}`,
      }}
      onClick={onClick}
    >
      {children}
      <CardContent className="p-3 flex items-center gap-3 h-full w-full">
        {customIcon || (
          <span className="text-muted-foreground">
            {getNodeIcon(nodeType, nodeColor, iconSize)}
          </span>
        )}
        <div className="flex flex-col min-w-0 flex-1">
          <span className="text-sm font-medium leading-tight truncate" title={title}>
            {title}
          </span>
          {subtitle && (
            <span className="text-xs text-muted-foreground">{subtitle}</span>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default NodeCard;
